import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Modal, ReduxControls, ProgressIndicator } from "rhombus-react";
import { reduxForm, Field } from "redux-form";
import { connect, useDispatch, useSelector } from "react-redux";

import { requiredNotNull } from "forms/validators/validators";
import { validVersionNameCharacters, versionNameMaxLength, versionNameExists } from "forms/validators/Versions";
import { createVersion, loadCreativeVersions, publishVersion, socialPublishVersion } from "reducers/CreativeVersions";

import Permissioned from "widgets/Permissioned";
import { VERSION_SAVE_AS_FORM } from "widgets/forms/list";
import { isSocialCreative, noop } from "utils/common";

import { loadCreative } from "reducers/CreativeDetails";

import "./SaveAsModal.css";

function SaveAsModal({
	libraryId,
	creativeId,
	version, // the Version object that we'll save with a new name

	onClose = noop,

	asyncValidating,
	valid,
	handleSubmit,
	...props
}){
	const dispatch = useDispatch();
	const history = useHistory();
	const [isSaving, setIsSaving] = useState(false);
	const creativeDetails = useSelector(state => state.creativeDetails) || {};
	const isSocial = isSocialCreative(creativeDetails);

	function saveNewVersion(values){
		const { name, publishAfterSaving } = values || {};
		setIsSaving(true);
		let newVersion = { ...version, name };

		dispatch(createVersion(libraryId, creativeId, newVersion))
			.then((response) => {
				setIsSaving(false);
				if(response?.success){
					/*
						The parent Creative will be updated, e.g. `numberOfVersions`, `decisioningType`, so we have to loadCreative
						after our other operations are done.
					*/
					let { id: newId } = response?.payload?.data || {};
					if(newId){
						if(publishAfterSaving){
							if (isSocial) {
								dispatch(socialPublishVersion([newId]))
									.then(() => {
										// After publishing, multiple Version rows will have their `status` updated, so we have to refetch
										dispatch(loadCreativeVersions(libraryId, creativeId));
										dispatch(loadCreative(creativeId));
									});
							} else {
								dispatch(publishVersion(libraryId, creativeId, newId))
									.then(() => {
										// After publishing, multiple Version rows will have their `status` updated, so we have to refetch
										dispatch(loadCreativeVersions(libraryId, creativeId));
										dispatch(loadCreative(creativeId));
									});
							}
						} else {
							dispatch(loadCreative(creativeId));
						}
						history.replace(`/library/${libraryId}/creative/${creativeId}/versions/${newId}/modal/edit-version/`);
					}
					onClose(newId);
				}
			});
	}

	function Body(){
		return (
			<div className="body-container">
				<form id={VERSION_SAVE_AS_FORM} onSubmit={handleSubmit(saveNewVersion)}>
					<ProgressIndicator isLoading={isSaving || asyncValidating} />
					<Field
						name = "name"
						label = "New Name for Version"
						component={ReduxControls.Input}
						validate = {[validVersionNameCharacters, versionNameMaxLength, requiredNotNull]}
					/>
					<Permissioned
						domain = "Version Library"
						action = "Publish version"
					>
						<Field
							name = "publishAfterSaving"
							label = "Publish after saving (this will remove any decisioning applied to other versions for this creative)"
							component = {ReduxControls.Checkbox}
							useValue
						/>
					</Permissioned>
				</form>
			</div>
		);
	}

	function Footer(){
		return (
			<>
				<Button theme="default" disabled={!!asyncValidating || isSaving} onClick={onClose}>Cancel</Button>
				<Button
					type = "submit"
					form = {VERSION_SAVE_AS_FORM}
					theme = "green"
					disabled = {!valid || !!asyncValidating || isSaving} // Button wants a pure bool, and asyncValidating will sometimes be a string
				>
					Save As New Version
				</Button>
			</>
		);
	}

	const modalProps = {
		title: "Save As...",
		type: "modal",
		className: "version-save-as-modal",
		xClose: true,
		allowFullScreen: false,
		setAppTitle: false,
		bladeProps: {
			verticallyCenter: true,
		},

		onClose,
		renderContent: Body,
		renderFooter: Footer,
	};

	return <Modal {...modalProps} />;
}

SaveAsModal.displayName = "SaveAsModal";

function mapStateToProps(state, ownProps){
	let { version } = ownProps;
	return {
		initialValues: {
			name: `${version?.name} copy`,
			publishAfterSaving: false,
		},
	};
}

function asyncValidate(values, dispatch, props){
	let { name } = values;
	let { libraryId, creativeId } = props;

	return versionNameExists(dispatch, libraryId, creativeId, name, "name");
}

const ConnectedSaveAsModal = reduxForm({
	form: VERSION_SAVE_AS_FORM,
	asyncValidate,
	asyncBlurFields: [], // This makes it just asyncValidate on submit
})(SaveAsModal);
export default connect(mapStateToProps, null)(ConnectedSaveAsModal);
